import React, { useState } from "react";
import { KeywordEntry, TriggerNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { NodeMenu } from "../../../shared/trigger-shared/node-menu";
import { KeywordRow } from "../../../shared/trigger-shared/keyword-row";
import { DeleteModal } from "../../../shared/trigger-shared/delete-modal";

const MAX_CHARS = 200;
const makeKwId = (count: number) => `kw_${count}`;

interface RcsTriggerCardProps {
  node: TriggerNode;
  onChange: (n: TriggerNode) => void;
  onDelete: () => void;
  onReset: () => void;
}

// Same keyword-list interaction as On Message — RCS trigger just replies
// to matching keywords too, defaulting to "hi"/"hello" like the sample flow.
export const RcsTriggerCard: React.FC<RcsTriggerCardProps> = ({ node, onChange, onDelete, onReset }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [keywords, setKeywords] = useState<KeywordEntry[]>(() =>
    node.keywords?.length
      ? node.keywords
      : [
          { id: "kw_1", text: "hi", match_similar: false },
          { id: "kw_2", text: "hello", match_similar: false },
        ],
  );
  const [restartJourney, setRestartJourney] = useState(node.restart_journey ?? false);

  const syncUp = (next: KeywordEntry[], restart: boolean) =>
    onChange({ ...node, keywords: next, restart_journey: restart });

  const updateEntry = (idx: number, updated: KeywordEntry) => {
    const next = keywords.map((kw, i) => (i === idx ? updated : kw));
    setKeywords(next);
    syncUp(next, restartJourney);
  };

  const addKeyword = () => {
    const next = [...keywords, { id: makeKwId(keywords.length + 1), text: "", match_similar: false }];
    setKeywords(next);
    syncUp(next, restartJourney);
  };

  const deleteKeyword = (idx: number) => {
    const next = keywords.filter((_, i) => i !== idx);
    setKeywords(next);
    syncUp(next, restartJourney);
  };

  const toggleRestart = () => {
    const next = !restartJourney;
    setRestartJourney(next);
    syncUp(keywords, next);
  };

  return (
    <>
      <div className="relative">
        <div className="absolute -top-7 left-0">
          <span className="bg-gray-800 text-white text-xs px-3 py-1 rounded-full">
            Bot Starting Point
          </span>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 w-[265px]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide">
              Trigger
            </span>
            <NodeMenu onDelete={() => setShowDeleteModal(true)} onReset={onReset} />
          </div>

          <p className="text-indigo-600 font-semibold text-sm mb-0.5">RCS Trigger</p>
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-3">
            Define message keywords to trigger this RCS bot
          </p>

          {keywords.map((entry, idx) => (
            <KeywordRow
              key={entry.id}
              entry={entry}
              isFirst={idx === 0}
              maxChars={MAX_CHARS}
              onChange={(updated) => updateEntry(idx, updated)}
              onDelete={idx > 0 ? () => deleteKeyword(idx) : undefined}
            />
          ))}

          <button
            onClick={addKeyword}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium py-2 rounded-lg mt-1 mb-3 transition-colors"
          >
            Add New Keyword
          </button>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={restartJourney}
              onChange={toggleRestart}
              className="mt-0.5 w-4 h-4 rounded accent-indigo-600 flex-shrink-0"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {keywords.length > 1
                ? "When this triggers, restart the user journey."
                : "Restart the user journey when this triggers."}
            </span>
          </label>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteModal
          title="Delete trigger?"
          description="This will remove the trigger and any connected flow from your bot journey. This action cannot be undone."
          confirmLabel="Delete Trigger"
          onConfirm={() => {
            onDelete();
            setShowDeleteModal(false);
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};
